import {
  DynamoDBClient,
  DynamoDBClientConfig,
  ScanCommand,
  ScanCommandInput,
  PutItemCommand,
  UpdateItemCommand,
  QueryCommand,
  GetItemCommand
} from '@aws-sdk/client-dynamodb';
import { marshall } from '@aws-sdk/util-dynamodb';
import { STSClient, AssumeRoleCommand } from '@aws-sdk/client-sts';
import { unmarshall } from '@aws-sdk/util-dynamodb';
import { ENVIRONMENT_VARIABLES } from 'src/constants/EnvironmentConstants';
import { CognitoService } from 'src/services/aws/CognitoService';

export class DynamoDBService {
  private static client: DynamoDBClient | null = null;

  private static getClient(): DynamoDBClient {
    if (this.client) return this.client;
    this.client = new DynamoDBClient({
      region: ENVIRONMENT_VARIABLES.env.Region,
      credentials: CognitoService.credentialProvider
    });
    return this.client;
  }

  /**
   * Get DynamoDB client with cross-account credentials via STS AssumeRole
   * @param roleArn - The IAM role ARN to assume in the target account
   * @param region - Optional region override
   */
  private static async getCrossAccountClient(roleArn: string, region?: string): Promise<DynamoDBClient> {
    // First, get base credentials from Cognito
    const baseCredentials = await CognitoService.getAWSServiceCredentials();

    // Create STS client with base credentials
    const stsClient = new STSClient({
      region: region || ENVIRONMENT_VARIABLES.env.Region,
      credentials: baseCredentials
    });

    // Assume the cross-account role
    const assumeRoleResponse = await stsClient.send(
      new AssumeRoleCommand({
        RoleArn: roleArn,
        RoleSessionName: `upgrade-medic-ui-${Date.now()}`
      })
    );

    if (!assumeRoleResponse.Credentials) {
      throw new Error(`Failed to assume role: ${roleArn}`);
    }

    // Create DynamoDB client with assumed role credentials
    return new DynamoDBClient({
      region: region || ENVIRONMENT_VARIABLES.env.Region,
      credentials: {
        accessKeyId: assumeRoleResponse.Credentials.AccessKeyId!,
        secretAccessKey: assumeRoleResponse.Credentials.SecretAccessKey!,
        sessionToken: assumeRoleResponse.Credentials.SessionToken
      }
    });
  }

  private static async scanTable(params: ScanCommandInput, result: any[] = []): Promise<any[]> {
    const client = this.getClient();
    const { Items, LastEvaluatedKey } = await client.send(new ScanCommand(params));
    const unmarshalledItems = Items?.map((i) => unmarshall(i)) ?? [];
    const finalResult = result.concat(unmarshalledItems);

    if (LastEvaluatedKey) {
      params.ExclusiveStartKey = LastEvaluatedKey;
      return this.scanTable(params, finalResult);
    }

    return finalResult;
  }

  /**
   * Scan table with cross-account client (for tables in different AWS accounts)
   */
  private static async scanTableCrossAccount(client: DynamoDBClient, params: ScanCommandInput, result: any[] = []): Promise<any[]> {
    const { Items, LastEvaluatedKey } = await client.send(new ScanCommand(params));
    const unmarshalledItems = Items?.map((i) => unmarshall(i)) ?? [];
    const finalResult = result.concat(unmarshalledItems);

    if (LastEvaluatedKey) {
      params.ExclusiveStartKey = LastEvaluatedKey;
      return this.scanTableCrossAccount(client, params, finalResult);
    }

    return finalResult;
  }

  /**
   * Get all items from a DynamoDB table in the SAME account
   * Uses Cognito Identity Pool credentials
   */
  public static async getAllItems(tableName: string, limit = 4000): Promise<any[]> {
    const scanInput: ScanCommandInput = {
      TableName: tableName,
      Limit: limit
    };

    return this.scanTable(scanInput);
  }

  /**
   * Get all items from a DynamoDB table in a DIFFERENT account
   * Uses STS AssumeRole for cross-account access
   *
   * @param tableName - The DynamoDB table name in the target account
   * @param roleArn - The IAM role ARN to assume (must have DynamoDB:Scan permission)
   * @param region - Optional region override
   * @param limit - Maximum items to scan (default 4000)
   * @returns Array of unmarshalled DynamoDB items
   */
  public static async getAllItemsCrossAccount(tableName: string, roleArn: string, region?: string, limit = 4000): Promise<any[]> {
    const client = await this.getCrossAccountClient(roleArn, region);

    const scanInput: ScanCommandInput = {
      TableName: tableName,
      Limit: limit
    };

    return this.scanTableCrossAccount(client, scanInput);
  }

  /**
   * Put an item into a DynamoDB table in a DIFFERENT account
   */
  public static async putItemCrossAccount(tableName: string, item: Record<string, any>, roleArn: string, region?: string): Promise<void> {
    const client = await this.getCrossAccountClient(roleArn, region);

    await client.send(
      new PutItemCommand({
        TableName: tableName,
        Item: marshall(item, { removeUndefinedValues: true })
      })
    );
  }

  /**
   * Update an item in a DynamoDB table in a DIFFERENT account
   */
  public static async updateItemCrossAccount(
    tableName: string,
    key: Record<string, any>,
    updates: Record<string, any>,
    roleArn: string,
    region?: string
  ): Promise<void> {
    const client = await this.getCrossAccountClient(roleArn, region);

    // Build UpdateExpression and ExpressionAttributeValues
    const updateParts: string[] = [];
    const expressionAttributeNames: Record<string, string> = {};
    const expressionAttributeValues: Record<string, any> = {};

    let attrIndex = 0;
    for (const [field, value] of Object.entries(updates)) {
      if (value !== undefined) {
        // Handle nested paths like "stages.BUILD" or "artifacts.crLink"
        const namePlaceholder = `#attr${attrIndex}`;
        const valuePlaceholder = `:val${attrIndex}`;

        if (field.includes('.')) {
          // For nested paths, split and use expression attribute names
          const parts = field.split('.');
          const nameKeys = parts.map((p, i) => {
            const key = `#p${attrIndex}_${i}`;
            expressionAttributeNames[key] = p;
            return key;
          });
          updateParts.push(`${nameKeys.join('.')} = ${valuePlaceholder}`);
        } else {
          expressionAttributeNames[namePlaceholder] = field;
          updateParts.push(`${namePlaceholder} = ${valuePlaceholder}`);
        }

        expressionAttributeValues[valuePlaceholder] = value;
        attrIndex++;
      }
    }

    if (updateParts.length === 0) return;

    await client.send(
      new UpdateItemCommand({
        TableName: tableName,
        Key: marshall(key),
        UpdateExpression: `SET ${updateParts.join(', ')}`,
        ExpressionAttributeNames: expressionAttributeNames,
        ExpressionAttributeValues: marshall(expressionAttributeValues, { removeUndefinedValues: true })
      })
    );
  }

  /**
   * Query items from a DynamoDB table in a DIFFERENT account
   * Uses scan with filter as fallback when GSI is not available
   */
  public static async queryItemsCrossAccount(
    tableName: string,
    filterField: string,
    filterValue: string,
    roleArn: string,
    region?: string,
    limit?: number
  ): Promise<any[]> {
    const client = await this.getCrossAccountClient(roleArn, region);

    const scanInput: ScanCommandInput = {
      TableName: tableName,
      FilterExpression: `#field = :value`,
      ExpressionAttributeNames: { '#field': filterField },
      ExpressionAttributeValues: marshall({ ':value': filterValue })
    };

    // Paginate through all results
    const allItems: any[] = [];
    let lastEvaluatedKey: Record<string, any> | undefined;

    do {
      if (lastEvaluatedKey) {
        scanInput.ExclusiveStartKey = lastEvaluatedKey;
      }

      const result = await client.send(new ScanCommand(scanInput));
      const items = result.Items?.map((item) => unmarshall(item)) ?? [];
      allItems.push(...items);
      lastEvaluatedKey = result.LastEvaluatedKey;

      // If a limit is specified and we've reached it, stop early
      if (limit && allItems.length >= limit) {
        return allItems.slice(0, limit);
      }
    } while (lastEvaluatedKey);

    return allItems;
  }

  /**
   * Get a single item from a DynamoDB table in a DIFFERENT account by primary key
   * @param tableName - The DynamoDB table name
   * @param key - The primary key (e.g., { jobId: 'xxx', itemType: 'JOB_METADATA' })
   * @param roleArn - The IAM role ARN to assume
   * @param region - Optional region override
   * @returns The unmarshalled item or null if not found
   */
  public static async getItemCrossAccount(tableName: string, key: Record<string, any>, roleArn: string, region?: string): Promise<any | null> {
    const client = await this.getCrossAccountClient(roleArn, region);

    const result = await client.send(
      new GetItemCommand({
        TableName: tableName,
        Key: marshall(key)
      })
    );

    if (!result.Item) {
      return null;
    }

    return unmarshall(result.Item);
  }
}
